import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, UserCircle2, Plus, Home,
  Briefcase, PackageOpen, Calendar, ChevronDown, ChevronRight,
  Notebook, Video, Mic, Megaphone, TrendingUp, Bell, BarChart3, FileSignature, Users, Shield,
  Zap, Target, Banknote, CalendarDays,
} from 'lucide-react';
import clsx from 'clsx';
import { BrandLogo } from './BrandLogo';
import { useAuth } from '@/lib/auth';
import type { Papel } from '@/lib/types';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  roles: Papel[];
}

// Ordem definitiva (P38.10):
// Dash Plataforma · Plataforma (com Inscritos como sub) · Crescimento · Demandas ⇣
// Entregas · Calendário de Eventos · Lembretes · Atalhos · Portais
// =====================================================================

const ITEM_DASH_PLATAFORMA: NavItem = {
  to: '/dash-comercial', label: 'Dash Plataforma', icon: BarChart3, roles: ['Coordenação', 'Diretoria', 'Comercial'],
};
const ITEM_INSCRITOS: NavItem = {
  to: '/inscricoes',     label: 'Inscritos',       icon: Users,       roles: ['Coordenação', 'Diretoria', 'Comercial', 'Executor'],
};
const ITEM_CRESCIMENTO: NavItem = {
  to: '/internos',       label: 'Crescimento',     icon: TrendingUp,  roles: ['Coordenação', 'Diretoria', 'Comercial'],
};

const DEMANDAS_SUBITEMS: { to: string; label: string; icon: typeof LayoutGrid }[] = [
  { to: '/demandas/analise',   label: 'Análise',   icon: TrendingUp },
  { to: '/demandas/conteudo',  label: 'Conteúdo',  icon: Megaphone },
  { to: '/demandas/branding',  label: 'Branding',  icon: Notebook },
  { to: '/demandas/produtora', label: 'Produtora', icon: Video },
  { to: '/demandas/eventos',   label: 'Eventos',   icon: Mic },
];

const MAC_SUBITEMS: { to: string; label: string; icon: typeof LayoutGrid }[] = [
  { to: '/mac/enxoval',       label: 'Enxoval',       icon: Target },
  { to: '/mac/planejamento',  label: 'Planejamento',  icon: CalendarDays },
  { to: '/mac/ofertas',       label: 'Ofertas',       icon: Banknote },
];

const NAV_GERAIS: NavItem[] = [
  { to: '/entregas',  label: 'Entregas',              icon: PackageOpen, roles: ['Coordenação', 'Diretoria', 'Comercial', 'Executor'] },
  { to: '/eventos',   label: 'Calendário de Eventos', icon: Calendar,    roles: ['Coordenação', 'Diretoria', 'Comercial', 'Executor'] },
  { to: '/lembretes', label: 'Lembretes',             icon: Bell,        roles: ['Coordenação', 'Diretoria', 'Comercial', 'Executor'] },
  { to: '/audit',     label: 'Audit Log',             icon: Shield,      roles: ['Coordenação', 'Diretoria'] },
];

const NAV_SHORTCUTS_CLIENT: NavItem[] = [
  { to: '/portal', label: 'Portal do Cliente', icon: UserCircle2, roles: ['Cliente'] },
];

export function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [demandasOpen, setDemandasOpen] = useState(
    location.pathname.startsWith('/demandas') ||
    location.pathname.startsWith('/producao') ||
    location.pathname.startsWith('/dash-producao'),
  );
  const [plataformaOpen, setPlataformaOpen] = useState(
    location.pathname.startsWith('/comercial') ||
    location.pathname.startsWith('/inscricoes'),
  );
  const [macOpen, setMacOpen] = useState(
    location.pathname.startsWith('/mac'),
  );

  if (!user) return null;

  if (user.papel === 'Cliente') {
    return (
      <aside className="w-[232px] bg-panel border-r border-border flex flex-col fixed inset-y-0 left-0 p-3">
        <Brand />
        <nav className="mt-4 space-y-0.5">
          {NAV_SHORTCUTS_CLIENT.map((it) => (
            <SidebarLink key={it.to} item={it} active={location.pathname.startsWith(it.to)} />
          ))}
        </nav>
      </aside>
    );
  }

  const canSeePlataforma = ['Coordenação', 'Diretoria', 'Comercial'].includes(user.papel);
  const canSeeInscritos = canSeePlataforma || user.papel === 'Executor';
  const canSeeCrescimento = canSeePlataforma;
  const canSeeDashPlataforma = user.papel === 'Coordenação' || user.papel === 'Diretoria' || user.canVerValores === true;
  const isStaff = user.papel === 'Coordenação' || user.papel === 'Diretoria' || user.papel === 'Executor';
  const AUDIT_EMAILS = new Set([
    'andakur@apexpartners.com.br',
    'frizerar@apexpartners.com.br',
    'gomesm@apexpartners.com.br',
    'renan@apexpartners.com.br',
  ]);
  const gerais = NAV_GERAIS.filter((i) => {
    if (i.to === '/audit') return AUDIT_EMAILS.has(user.email.toLowerCase());
    return i.roles.includes(user.papel);
  });

  function openNovaDemanda() {
    navigate('/demandas?nova=1');
  }

  return (
    <aside className="w-[232px] bg-panel border-r border-border flex flex-col fixed inset-y-0 left-0 p-3 overflow-y-auto">
      <Brand />

      <nav className="mt-4 space-y-0.5">
        {/* 0 · Home */}
        <SidebarLink
          item={{ to: '/home', label: 'Home', icon: Home, roles: ['Coordenação', 'Diretoria', 'Executor', 'Comercial'] }}
          active={location.pathname === '/home'}
        />

        {/* 1 · Dash Plataforma */}
        {canSeeDashPlataforma && (
          <SidebarLink item={ITEM_DASH_PLATAFORMA} active={location.pathname === '/dash-comercial'} />
        )}

        {/* 2 · Plataforma (com Inscritos como sub) */}
        {canSeePlataforma && (
          <div>
            <button
              type="button"
              onClick={() => setPlataformaOpen((v) => !v)}
              className={clsx(
                'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-bold transition-colors border',
                location.pathname === '/comercial'
                  ? 'bg-blue/15 text-blue border-blue/30'
                  : 'text-text-2 hover:bg-panel-2 hover:text-text border-transparent',
              )}
            >
              <Briefcase className="w-4 h-4 opacity-80" />
              <span className="flex-1 text-left">
                <NavLink to="/comercial" onClick={(e) => e.stopPropagation()} className="hover:underline">Plataforma</NavLink>
              </span>
              {plataformaOpen
                ? <ChevronDown className="w-3 h-3 opacity-70" />
                : <ChevronRight className="w-3 h-3 opacity-70" />}
            </button>
            {plataformaOpen && (
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
                <NavLink
                  to="/comercial?nova=1"
                  className={clsx(
                    'flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
                    'text-text-2 hover:bg-panel-2 hover:text-text',
                  )}
                >
                  <FileSignature className="w-3 h-3 opacity-70" />
                  Novo contrato
                </NavLink>
                {canSeeInscritos && (
                  <NavLink
                    to={ITEM_INSCRITOS.to}
                    className={clsx(
                      'flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
                      location.pathname === ITEM_INSCRITOS.to
                        ? 'bg-blue/15 text-blue'
                        : 'text-text-2 hover:bg-panel-2 hover:text-text',
                    )}
                  >
                    <Users className="w-3 h-3 opacity-70" />
                    Inscritos
                  </NavLink>
                )}
              </div>
            )}
          </div>
        )}

        {/* 2b · Inscritos standalone (Executor que não vê Plataforma) */}
        {canSeeInscritos && !canSeePlataforma && (
          <SidebarLink item={ITEM_INSCRITOS} active={location.pathname === ITEM_INSCRITOS.to} />
        )}

        {/* 3 · Crescimento */}
        {canSeeCrescimento && (
          <SidebarLink item={ITEM_CRESCIMENTO} active={location.pathname === '/internos'} />
        )}

        {/* 4 · Demandas (accordion) */}
        {isStaff && (
          <div>
            <button
              type="button"
              onClick={() => setDemandasOpen((v) => !v)}
              className={clsx(
                'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-bold transition-colors border',
                location.pathname === '/demandas' || location.pathname === '/producao'
                  ? 'bg-blue/15 text-blue border-blue/30'
                  : 'text-text-2 hover:bg-panel-2 hover:text-text border-transparent',
              )}
            >
              <LayoutGrid className="w-4 h-4 opacity-80" />
              <span className="flex-1 text-left">
                <NavLink to="/demandas" onClick={(e) => e.stopPropagation()} className="hover:underline">Demandas</NavLink>
              </span>
              {demandasOpen
                ? <ChevronDown className="w-3 h-3 opacity-70" />
                : <ChevronRight className="w-3 h-3 opacity-70" />}
            </button>
            {demandasOpen && (
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
                {DEMANDAS_SUBITEMS.map((it) => {
                  const Icon = it.icon;
                  const active = location.pathname === it.to;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      className={clsx(
                        'flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
                        active
                          ? 'bg-blue/15 text-blue'
                          : 'text-text-2 hover:bg-panel-2 hover:text-text',
                      )}
                    >
                      <Icon className="w-3 h-3 opacity-70" />
                      {it.label}
                    </NavLink>
                  );
                })}
                <NavLink
                  to="/dash-producao"
                  className={clsx(
                    'flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-medium transition-colors mt-1 border-t border-border pt-2',
                    location.pathname === '/dash-producao'
                      ? 'bg-blue/15 text-blue'
                      : 'text-text-2 hover:bg-panel-2 hover:text-text',
                  )}
                >
                  <BarChart3 className="w-3 h-3 opacity-70" />
                  Dash Demandas
                </NavLink>
              </div>
            )}
          </div>
        )}

        {/* 5 · MAC (accordion) */}
        {isStaff && (
          <div>
            <button
              type="button"
              onClick={() => setMacOpen((v) => !v)}
              className={clsx(
                'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-bold transition-colors border',
                location.pathname.startsWith('/mac')
                  ? 'bg-blue/15 text-blue border-blue/30'
                  : 'text-text-2 hover:bg-panel-2 hover:text-text border-transparent',
              )}
            >
              <Zap className="w-4 h-4 opacity-80" />
              <span className="flex-1 text-left">
                <NavLink to="/mac/enxoval" onClick={(e) => e.stopPropagation()} className="hover:underline">MAC</NavLink>
              </span>
              {macOpen
                ? <ChevronDown className="w-3 h-3 opacity-70" />
                : <ChevronRight className="w-3 h-3 opacity-70" />}
            </button>
            {macOpen && (
              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
                {MAC_SUBITEMS.map((it) => {
                  const Icon = it.icon;
                  const active = location.pathname === it.to;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      className={clsx(
                        'flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-medium transition-colors',
                        active
                          ? 'bg-blue/15 text-blue'
                          : 'text-text-2 hover:bg-panel-2 hover:text-text',
                      )}
                    >
                      <Icon className="w-3 h-3 opacity-70" />
                      {it.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 6+ · Entregas / Calendário / Lembretes */}
        <div className="mt-1">
          {gerais.map((it) => (
            <SidebarLink key={it.to} item={it} active={location.pathname.startsWith(it.to)} />
          ))}
        </div>
      </nav>

      <div className="mt-5">
        <div className="text-[9px] font-extrabold uppercase tracking-wider text-muted px-2 pb-1.5">
          Atalhos
        </div>
        <button
          type="button"
          onClick={openNovaDemanda}
          className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-medium text-text-2 hover:bg-panel-2 hover:text-text transition-colors"
        >
          <Plus className="w-4 h-4 opacity-80" />
          Nova demanda
        </button>
        <NavLink
          to="/portais"
          className={clsx(
            'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-medium transition-colors',
            location.pathname.startsWith('/portal') || location.pathname.startsWith('/portais')
              ? 'bg-blue/15 text-blue'
              : 'text-text-2 hover:bg-panel-2 hover:text-text',
          )}
        >
          <UserCircle2 className="w-4 h-4 opacity-80" />
          Portais
        </NavLink>
      </div>
    </aside>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-1 pb-3 mb-1 border-b border-border">
      <BrandLogo size={28} />
      <div className="font-extrabold text-[13px] tracking-tight">Research</div>
    </div>
  );
}

function SidebarLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={clsx(
        'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-bold transition-colors border',
        active
          ? 'bg-blue/15 text-blue border-blue/30'
          : 'text-text-2 hover:bg-panel-2 hover:text-text border-transparent',
      )}
    >
      <Icon className="w-4 h-4 opacity-80" />
      {item.label}
    </NavLink>
  );
}
